import api from './api';
import type {
  ContainerMonitor,
  StatusChangeLog,
  MonitorStats,
  PccCheckResult,
} from '../types/scraper';

export const scraperApi = {
  // ==================== Monitors ====================

  async getMonitors(filters?: {
    status?: string;
    terminal?: string;
    alert?: string;
    containerStatus?: string;
  }): Promise<ContainerMonitor[]> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.terminal) params.append('terminal', filters.terminal);
    if (filters?.alert) params.append('alert', filters.alert);
    if (filters?.containerStatus) params.append('containerStatus', filters.containerStatus);
    const qs = params.toString();
    const response = await api.get(`/scraper/monitors${qs ? `?${qs}` : ''}`);
    return response.data;
  },

  async getMonitorById(id: number): Promise<ContainerMonitor> {
    const response = await api.get(`/scraper/monitors/${id}`);
    return response.data;
  },

  async triggerCheck(id: number): Promise<ContainerMonitor> {
    const response = await api.post(`/scraper/monitors/${id}/check`);
    return response.data;
  },

  /**
   * Queue a batched refresh. Resolves once the selection is queued, NOT once
   * the terminals have answered — a sweep takes minutes and this client gives
   * up after ten seconds. The panel's own poll is what fills the rows in.
   */
  async triggerBatchCheck(monitorIds: number[]): Promise<{ queued: number; sweeping: boolean }> {
    const response = await api.post('/scraper/monitors/batch-check', { monitorIds });
    return response.data;
  },

  /**
   * Ask PCC about one container right now (customs exam station, not a
   * terminal). Unlike the terminal check this resolves with the ANSWER, not
   * with "queued": one PCC lookup is three requests and the usual answer —
   * "no record yet" — changes nothing on the row, so it has to be said out
   * loud or the button reads as broken.
   *
   * Hence the explicit timeout: the shared 10s default is shorter than a slow
   * three-hop lookup, and timing out here would report a failure for a query
   * that actually succeeded.
   */
  async triggerPccCheck(id: number): Promise<PccCheckResult> {
    const response = await api.post(`/scraper/monitors/${id}/pcc-check`, undefined, {
      timeout: 60000,
    });
    return response.data;
  },

  /**
   * 这个任务的柜号在 PCC 的结果页地址 —— 板上 CBP Hold 那一行的 LFD 点一下
   * 就走这里，拿到地址后由浏览器打开。
   *
   * 为什么不是前端自己拼：钥匙存在后端、永不下发，而结果页是按「这一票的
   * 流水号」寻址的，那个号只有提交一次查询才换得到。所以这一下是真的去打了
   * PCC 两个请求 —— 比「↻ PCC」那个按钮少一个，但同样要有一台在线的浏览器，
   * 也同样要给它一个宽一点的超时。
   */
  async getPccResultPageUrl(taskId: number | string): Promise<{ url: string; containerNumber: string }> {
    const response = await api.post(`/scraper/tasks/${taskId}/pcc-page`, undefined, {
      timeout: 60000,
    });
    return response.data;
  },

  async getHistory(id: number, limit?: number): Promise<StatusChangeLog[]> {
    const params = limit ? `?limit=${limit}` : '';
    const response = await api.get(`/scraper/monitors/${id}/history${params}`);
    return response.data;
  },

  async stopMonitor(id: number, reason?: string): Promise<ContainerMonitor> {
    const response = await api.post(`/scraper/monitors/${id}/stop`, { reason });
    return response.data;
  },

  async batchStopMonitors(monitorIds: number[], reason?: string): Promise<{ id: number; success: boolean }[]> {
    const response = await api.post('/scraper/monitors/batch-stop', { monitorIds, reason });
    return response.data;
  },

  async startMonitor(containerNumber: string, orderId?: string, taskId?: number): Promise<ContainerMonitor> {
    const response = await api.post('/scraper/monitors/start', { containerNumber, orderId, taskId });
    return response.data;
  },

  // ==================== Stats & Changes ====================

  async getStats(): Promise<MonitorStats> {
    const response = await api.get('/scraper/stats');
    return response.data;
  },

  async getRecentChanges(limit?: number): Promise<StatusChangeLog[]> {
    const params = limit ? `?limit=${limit}` : '';
    const response = await api.get(`/scraper/recent-changes${params}`);
    return response.data;
  },

  // ==================== Engine ====================

  async getEngineStatus(): Promise<Record<string, unknown>> {
    const response = await api.get('/scraper/engine-status');
    return response.data;
  },
};

export default scraperApi;
